import { useState } from "react";
import { Send, Loader2, BookUser, Plus, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ExtraServicesEditor from "@/components/ExtraServicesEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyAddr = { name: "", company: "", address: "", city: "", cap: "", province: "", phone: "" };
const emptyCollo = () => ({ id: Date.now(), tipo: "collo", peso: "", dimensioni: "", quantita: 1 });
const inputCls = "w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body";
const lblCls = "block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading";

function AddrFields({ data, onChange, label, addresses, showBook, onToggleBook, fillFromAddress, type, saveToBook, onSaveToBookChange }) {
  // Unified address book: show ALL saved addresses regardless of original "type" tag.
  // This avoids creating duplicates when the same address is used as sender or recipient.
  const filtered = addresses;
  const set = (field, value) => onChange({ ...data, [field]: value });
  // Hide save checkbox if address already in book (same name+address+city, any type)
  const alreadyInBook = filtered.some(a =>
    a.name?.trim().toLowerCase() === data.name?.trim().toLowerCase() &&
    a.address?.trim().toLowerCase() === data.address?.trim().toLowerCase() &&
    a.city?.trim().toLowerCase() === data.city?.trim().toLowerCase()
  );
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] font-heading">{label}</p>
        {filtered.length > 0 && (
          <button type="button" onClick={onToggleBook}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-zinc-500 hover:text-[#FF5200] transition-colors"
            data-testid={`select-from-book-${type}`}>
            <BookUser size={14} /> Da rubrica
          </button>
        )}
      </div>
      {showBook && (
        <div className="mb-4 border-2 border-zinc-200 bg-[#FAFAFA] p-3 space-y-2 max-h-48 overflow-y-auto" data-testid={`address-picker-${type}`}>
          {filtered.map((a) => (
            <button key={a.id} type="button" onClick={() => fillFromAddress(a, type)}
              className="w-full text-left p-2 hover:bg-white border border-transparent hover:border-zinc-200 transition-colors rounded-sm">
              <p className="font-bold text-sm text-[#09090B]">{a.name} {a.company && `- ${a.company}`}</p>
              <p className="text-xs text-zinc-500">{a.address}, {a.cap} {a.city} ({a.province})</p>
            </button>
          ))}
        </div>
      )}
      <div>
        <label className={lblCls}>Nome / Ragione sociale *</label>
        <input type="text" value={data.name} onChange={(e) => set("name", e.target.value)}
          className={inputCls} placeholder="Nome e cognome o ragione sociale" data-testid={`${type}-name-input`} />
      </div>
      <div className="mt-3">
        <label className={lblCls}>Indirizzo *</label>
        <input type="text" value={data.address} onChange={(e) => set("address", e.target.value)}
          className={inputCls} placeholder="Via/Piazza, numero civico" data-testid={`${type}-address-input`} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div>
          <label className={lblCls}>Citta *</label>
          <input type="text" value={data.city} onChange={(e) => set("city", e.target.value)}
            className={inputCls} placeholder="Citta" data-testid={`${type}-city-input`} />
        </div>
        <div>
          <label className={lblCls}>CAP *</label>
          <input type="text" value={data.cap} onChange={(e) => set("cap", e.target.value)}
            className={inputCls} placeholder="00000" data-testid={`${type}-cap-input`} />
        </div>
        <div>
          <label className={lblCls}>Provincia</label>
          <input type="text" value={data.province} onChange={(e) => set("province", e.target.value)}
            className={inputCls} placeholder="XX" data-testid={`${type}-province-input`} />
        </div>
      </div>
      <div className={`mt-3 ${type === "destinatario" ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}`}>
        <div>
          <label className={lblCls}>Telefono</label>
          <input type="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)}
            className={inputCls} placeholder="+39 XXX XXX XXXX" data-testid={`${type}-phone-input`} />
        </div>
        {type === "destinatario" && (
          <div>
            <label className={lblCls}>Email destinatario <span className="normal-case text-zinc-400 font-normal">(opzionale)</span></label>
            <input type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)}
              className={inputCls} placeholder="destinatario@azienda.it" data-testid="dest-email-input" />
            <p className="text-[10px] text-zinc-400 mt-1">Riceverà un'email alla creazione, al ritiro della merce e quando è in consegna.</p>
          </div>
        )}
      </div>
      <div className="mt-4 pt-3 border-t border-zinc-100">
        {alreadyInBook ? (
          <p className="text-xs text-zinc-400 italic flex items-center gap-1.5">
            <BookUser size={12} /> Indirizzo gia presente in rubrica
          </p>
        ) : (
          <label className="flex items-center gap-2 cursor-pointer group" data-testid={`save-to-book-${type}`}>
            <input type="checkbox" checked={saveToBook} onChange={(e) => onSaveToBookChange(e.target.checked)}
              className="w-4 h-4 accent-[#FF5200]" />
            <span className="text-xs font-bold uppercase tracking-wide text-zinc-600 group-hover:text-[#FF5200] flex items-center gap-1.5">
              <BookUser size={14} /> Salva in rubrica per usarlo di nuovo
            </span>
          </label>
        )}
      </div>
    </div>
  );
}

export default function NewShipmentForm({ token, addresses, onCreated, mode = "spedizione" }) {
  const isPickup = mode === "ritiro";
  const [origin, setOrigin] = useState({ ...emptyAddr });
  const [dest, setDest] = useState({ ...emptyAddr });
  const [colli, setColli] = useState([emptyCollo()]);
  const [serviceType, setServiceType] = useState("trasporto");
  const [urgency, setUrgency] = useState("normale");
  const [goodsReadyMode, setGoodsReadyMode] = useState("none");
  const [goodsReadyTime, setGoodsReadyTime] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [ddtFiles, setDdtFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOriginBook, setShowOriginBook] = useState(false);
  const [showDestBook, setShowDestBook] = useState(false);
  const [saveOrigin, setSaveOrigin] = useState(false);
  const [saveDest, setSaveDest] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const [insuranceValue, setInsuranceValue] = useState("");
  const [note, setNote] = useState("");
  const [descrizioneMerce, setDescrizioneMerce] = useState("");
  const [extraServices, setExtraServices] = useState({});

  const addCollo = () => setColli([...colli, emptyCollo()]);
  const removeCollo = (id) => { if (colli.length > 1) setColli(colli.filter((c) => c.id !== id)); };
  const updateCollo = (id, field, value) => setColli(colli.map((c) => c.id === id ? { ...c, [field]: value } : c));

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleDdtChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const invalid = files.find((file) => file.type !== "application/pdf");
    if (invalid) {
      toast.error("Puoi caricare solo file PDF");
      event.target.value = "";
      return;
    }

    try {
      const encoded = await Promise.all(files.map(async (file) => ({
        filename: file.name,
        content_type: file.type,
        data_base64: await toBase64(file),
      })));
      setDdtFiles((prev) => [...prev, ...encoded]);
    } catch {
      toast.error("Errore nel caricamento dei PDF");
    } finally {
      event.target.value = "";
    }
  };

  const removeDdt = (index) => setDdtFiles((prev) => prev.filter((_, idx) => idx !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!origin.name || !origin.address || !origin.city || !origin.cap) {
      toast.error("Completa i dati del mittente (nome, indirizzo, città, CAP)");
      return;
    }
    if (!dest.name || !dest.address || !dest.city || !dest.cap) {
      toast.error("Completa i dati del destinatario (nome, indirizzo, città, CAP)");
      return;
    }
    if (goodsReadyMode === "ready_at" && !goodsReadyTime) {
      toast.error("Inserisci l'orario per 'Merce pronta alle'");
      return;
    }
    if (insurance && !insuranceValue) {
      toast.error("Inserisci il valore della merce da assicurare");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isPickup ? `${API}/pickups/create` : `${API}/shipments/create`;
      const res = await axios.post(endpoint, {
        origin_name: origin.name, origin_company: origin.company,
        origin_address: origin.address, origin_city: origin.city,
        origin_cap: origin.cap, origin_province: origin.province, origin_phone: origin.phone,
        dest_name: dest.name, dest_company: dest.company,
        dest_address: dest.address, dest_city: dest.city,
        dest_cap: dest.cap, dest_province: dest.province, dest_phone: dest.phone,
        dest_email: dest.email || "",
        colli: colli.map(({ tipo, peso, dimensioni, quantita }) => ({ tipo, peso, dimensioni, quantita: quantita || 1 })),
        service_type: serviceType, urgency,
        scheduled_date: scheduledDate,
        goods_ready_status: goodsReadyMode !== "none" ? "pronta" : "non_pronta",
        goods_ready_time: goodsReadyMode === "ready_at" ? goodsReadyTime : "",
        ddt_files: ddtFiles,
        insurance,
        insurance_value: insurance ? insuranceValue : "",
        note,
        descrizione_merce: descrizioneMerce,
        extra_services: extraServices,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Save addresses to book if requested. Dedup by name+address+city.
      const addrExists = (v) => addresses.some((a) =>
        a.name?.trim().toLowerCase() === v.name?.trim().toLowerCase() &&
        a.address?.trim().toLowerCase() === v.address?.trim().toLowerCase() &&
        a.city?.trim().toLowerCase() === v.city?.trim().toLowerCase()
      );
      const saveToBookCalls = [];
      if (saveOrigin && origin.name && origin.address && origin.city && origin.cap && !addrExists(origin)) {
        saveToBookCalls.push(
          axios.post(`${API}/addresses`, {
            type: "generico",
            name: origin.name, company: origin.company || "",
            address: origin.address, city: origin.city,
            cap: origin.cap, province: origin.province || "",
            phone: origin.phone || "",
          }, { headers: { Authorization: `Bearer ${token}` } })
        );
      }
      if (saveDest && dest.name && dest.address && dest.city && dest.cap && !addrExists(dest)) {
        // Only save if different from origin (don't create two entries for same place)
        const sameAsOrigin =
          dest.name?.trim().toLowerCase() === origin.name?.trim().toLowerCase() &&
          dest.address?.trim().toLowerCase() === origin.address?.trim().toLowerCase() &&
          dest.city?.trim().toLowerCase() === origin.city?.trim().toLowerCase();
        if (!sameAsOrigin) {
          saveToBookCalls.push(
            axios.post(`${API}/addresses`, {
              type: "generico",
              name: dest.name, company: dest.company || "",
              address: dest.address, city: dest.city,
              cap: dest.cap, province: dest.province || "",
              phone: dest.phone || "",
            }, { headers: { Authorization: `Bearer ${token}` } })
          );
        }
      }
      if (saveToBookCalls.length) {
        try {
          await Promise.all(saveToBookCalls);
          toast.success("Indirizzi salvati in rubrica");
        } catch { /* non-blocking */ }
      }
      toast.success(`${isPickup ? "Ritiro" : "Spedizione"} ${res.data.tracking_number} creato con successo!`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Errore nella creazione ${isPickup ? "del ritiro" : "della spedizione"}`);
    } finally {
      setLoading(false);
    }
  };

  const fillAddr = (addr, type) => {
    const val = {
      name: addr.name, company: addr.company || "", address: addr.address,
      city: addr.city, cap: addr.cap, province: addr.province || "", phone: addr.phone || "",
    };
    if (type === "mittente") { setOrigin(val); setShowOriginBook(false); }
    else { setDest(val); setShowDestBook(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="new-shipment-form">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="border-2 border-zinc-200 p-6">
          <AddrFields data={origin} onChange={setOrigin} label={isPickup ? "Ritiro da" : "Mittente"} addresses={addresses} showBook={showOriginBook} onToggleBook={() => setShowOriginBook(!showOriginBook)} fillFromAddress={fillAddr} type="mittente" saveToBook={saveOrigin} onSaveToBookChange={setSaveOrigin} />
        </div>
        <div className="border-2 border-zinc-200 p-6">
          <AddrFields data={dest} onChange={setDest} label={isPickup ? "Consegna a" : "Destinatario"} addresses={addresses} showBook={showDestBook} onToggleBook={() => setShowDestBook(!showDestBook)} fillFromAddress={fillAddr} type="destinatario" saveToBook={saveDest} onSaveToBookChange={setSaveDest} />
        </div>
      </div>

      {/* Service + Urgency */}
      <div className="border-2 border-zinc-200 p-6">
        <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] mb-4 font-heading">Dettagli {isPickup ? "Ritiro" : "Spedizione"}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Tipo Servizio *</label>
            <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}
              className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body bg-white"
              data-testid="service-type-select">
              <option value="trasporto">Trasporto</option>
              <option value="logistica">Logistica</option>
              <option value="magazzino">Magazzino</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Urgenza</label>
            <select value={urgency} onChange={(e) => setUrgency(e.target.value)}
              className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body bg-white"
              data-testid="urgency-select">
              <option value="normale">Normale</option>
              <option value="urgente">Urgente</option>
              <option value="programmata">Programmata</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Data</label>
            <input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body bg-white"
              data-testid="scheduled-date-input" />
          </div>
          <div>
            <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Assicurazione</label>
            <div className="flex items-stretch gap-0">
              <button type="button"
                onClick={() => { setInsurance(!insurance); if (insurance) setInsuranceValue(""); }}
                className={`flex-shrink-0 h-11 px-4 border-2 rounded-l-sm text-sm font-bold uppercase tracking-wide transition-colors ${insurance ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                data-testid="insurance-toggle-btn">
                {insurance ? "Assicurata" : "Non assicurata"}
              </button>
              <input type="text" inputMode="decimal" value={insuranceValue}
                onChange={(e) => setInsuranceValue(e.target.value)}
                disabled={!insurance}
                placeholder="Valore merce (EUR)"
                className={`flex-1 h-11 px-3 border-2 border-l-0 rounded-r-sm text-sm font-body outline-none ${insurance ? "border-[#FF5200] bg-white" : "border-zinc-200 bg-zinc-50"} focus:border-[#FF5200] disabled:opacity-50`}
                data-testid="insurance-value-input" />
            </div>
          </div>
          <div className="col-span-1 md:col-span-2">
            <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">Merce pronta</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button type="button"
                onClick={() => { setGoodsReadyMode("ready_now"); setGoodsReadyTime(""); }}
                className={`h-12 px-4 border-2 rounded-sm text-sm font-bold uppercase tracking-wide transition-colors ${goodsReadyMode === "ready_now" ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                data-testid="goods-ready-now-btn">
                Merce pronta
              </button>
              <div className="flex items-stretch gap-0">
                <button type="button"
                  onClick={() => setGoodsReadyMode("ready_at")}
                  className={`flex-shrink-0 px-4 border-2 border-r-0 rounded-l-sm text-sm font-bold uppercase tracking-wide transition-colors ${goodsReadyMode === "ready_at" ? "border-[#FF5200] bg-[#FF5200] text-white" : "border-zinc-200 text-zinc-600 hover:border-[#FF5200]"}`}
                  data-testid="goods-ready-at-btn">
                  Pronta alle
                </button>
                <input type="time" value={goodsReadyTime}
                  onChange={(e) => { setGoodsReadyTime(e.target.value); setGoodsReadyMode("ready_at"); }}
                  disabled={goodsReadyMode !== "ready_at"}
                  className={`flex-1 h-12 px-3 border-2 rounded-r-sm text-sm font-body outline-none ${goodsReadyMode === "ready_at" ? "border-[#FF5200]" : "border-zinc-200 bg-zinc-50"} focus:border-[#FF5200] disabled:opacity-50`}
                  data-testid="goods-ready-time-input" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Colli List */}
      <div className="border-2 border-zinc-200 p-6" data-testid="colli-section">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] font-heading">
            Colli ({colli.reduce((sum, c) => sum + (parseInt(c.quantita) || 1), 0)})
          </p>
          <button type="button" onClick={addCollo}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#FF5200] hover:text-[#e64a00] transition-colors font-heading"
            data-testid="add-collo-btn">
            <Plus size={14} /> Aggiungi Collo
          </button>
        </div>

        <div className="space-y-3">
          {colli.map((c, idx) => (
            <div key={c.id} className="flex items-start gap-3 p-4 bg-[#FAFAFA] border border-zinc-200" data-testid={`collo-row-${idx}`}>
              <div className="flex items-center justify-center w-8 h-8 bg-[#FF5200] text-white text-xs font-bold rounded-sm flex-shrink-0 mt-5">
                {idx + 1}
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Tipo *</label>
                  <select value={c.tipo} onChange={(e) => updateCollo(c.id, "tipo", e.target.value)}
                    className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body bg-white"
                    data-testid={`collo-tipo-${idx}`}>
                    <option value="collo">Collo</option>
                    <option value="pallet">Pallet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">N. Colli</label>
                  <input type="number" min="1" value={c.quantita} onChange={(e) => updateCollo(c.id, "quantita", parseInt(e.target.value) || 1)}
                    className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body"
                    placeholder="1" data-testid={`collo-quantita-${idx}`} />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Peso (kg)</label>
                  <input type="text" value={c.peso} onChange={(e) => updateCollo(c.id, "peso", e.target.value)}
                    className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body"
                    placeholder="es. 50" data-testid={`collo-peso-${idx}`} />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Dimensioni (L x P x H)</label>
                  <input type="text" value={c.dimensioni} onChange={(e) => updateCollo(c.id, "dimensioni", e.target.value)}
                    className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body"
                    placeholder="es. 120x80x60 cm" data-testid={`collo-dimensioni-${idx}`} />
                </div>
              </div>
              {colli.length > 1 && (
                <button type="button" onClick={() => removeCollo(c.id)}
                  className="p-2 text-zinc-300 hover:text-red-500 transition-colors mt-5 flex-shrink-0"
                  data-testid={`remove-collo-${idx}`}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="border-2 border-zinc-200 p-6" data-testid="customer-ddt-upload-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-sm font-bold tracking-widest uppercase text-[#FF5200] font-heading">DDT merce (PDF)</p>
          <label className="inline-flex items-center gap-2 bg-[#09090B] text-white px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-sm cursor-pointer" data-testid="upload-ddt-trigger">
            <Plus size={14} /> Carica PDF
            <input type="file" accept="application/pdf" multiple className="hidden" onChange={handleDdtChange} data-testid="ddt-file-input" />
          </label>
        </div>
        {ddtFiles.length === 0 ? (
          <p className="text-sm text-zinc-500">Nessun DDT caricato.</p>
        ) : (
          <div className="space-y-2">
            {ddtFiles.map((file, idx) => (
              <div key={`${file.filename}-${idx}`} className="flex items-center justify-between gap-3 bg-[#FAFAFA] border border-zinc-200 p-3" data-testid={`ddt-file-row-${idx}`}>
                <div>
                  <p className="text-sm font-bold text-[#09090B]">{file.filename}</p>
                  <p className="text-xs text-zinc-500">PDF pronto per il salvataggio</p>
                </div>
                <button type="button" onClick={() => removeDdt(idx)} className="p-2 text-zinc-300 hover:text-red-500" data-testid={`remove-ddt-file-${idx}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-2 border-zinc-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="customer-merce" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">Dettaglio Merce</label>
          <input
            id="customer-merce"
            type="text"
            value={descrizioneMerce}
            onChange={(e) => setDescrizioneMerce(e.target.value)}
            placeholder="Es. componenti meccanici, mobili, ricambi..."
            className="w-full h-11 px-3 border-2 border-zinc-200 rounded-sm text-sm font-body outline-none focus:border-[#FF5200]"
            data-testid="customer-descrizione-merce-input"
          />
        </div>
        <div>
          <label htmlFor="customer-note" className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-2 font-heading">Note</label>
          <textarea
            id="customer-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={1}
            placeholder="Istruzioni speciali, riferimenti, indicazioni..."
            className="w-full px-3 py-2 border-2 border-zinc-200 rounded-sm text-sm font-body outline-none focus:border-[#FF5200] resize-none"
            data-testid="customer-note-input"
          />
        </div>
      </div>

      <ExtraServicesEditor
        value={extraServices}
        onChange={setExtraServices}
        showPrices={false}
        testIdPrefix="customer-extras"
      />

      <button type="submit" disabled={loading}
        className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors duration-200 font-heading text-sm disabled:opacity-50"
        data-testid="create-shipment-btn">
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {isPickup ? "Crea Ritiro" : "Crea Spedizione"}
      </button>
    </form>
  );
}
